import { FetchPolicy } from '@apollo/client';

type MathSymbols = '=' | '!=' | '>' | '>=' | '<' | '<=' | '*';
export type AlertVariant = (typeof ALERT_VARIANTS)[keyof typeof ALERT_VARIANTS];

export const SHOWRUNNR_AI_ID = '58e5fe1d-205d-3ba0-b7a4-e66c4ceb7492';

export const BEARER_TOKEN_ID = 'Bearer ';

export const DEFAULT_URL = 'https://';
export const DEFAULT_LANG = 'en-US';
export const DEFAULT_CURRENCY_CODE = 'USD';
export const PROFILE = 'profile';
export const USER_DATA = 'userData';
export const TP_DATA = 'tp';
export const TRANSFER_FLAG = 'TRANSFER_IN_PROGRESS';
export const OTHER = 'Other';
export const DEFAULT_SORT_FIELD = 'defaultSortField';
export const LABEL = 'label';
export const CONTACT = 'contact';
export const REF = 'ref';
export const REFERRER_LOCAL_STORAGE_KEY = 'referrer';
export const REFERRER_PREFIX = `${REFERRER_LOCAL_STORAGE_KEY}.`;
export const OWNER_ID = 'ownerId';
export const STAMP_METADATA_HEADER = 'X-Stamp-Metadata';
export const TIMEOUT_DELAY = 500;
export const IG = 'IG_';
export const DEFAULT_ATTRIBUTE_INDEX = 0;
export const SET_LOGIC_DOWNLOAD_LINK = 'https://static.app.showrunnr.com/setlogic/sr-latest';
export const POWERED_BY_SR = 'Powered by Showrunnr';
export const EXAMPLE_URL = 'https://example.com';
export const SECURE_ATTACHMENT_DOWNLOAD_ROUTE_PATH = '/files/secure/:assetId';

export const ROUTE_LABELS = {
    home: 'Home',
    workspace: 'Workspace'
};
export const URL_ATTRIBUTES = {
    rel: 'noopener',
    target: '_blank'
};
export const AKA = 'AKA';

export const IDENTIFIER = 'identifier';
export const FILTER_CONFIG = 'filter-config';

export const FILTER_IDENTIFIERS = {
    identity: 'identity',
    entity: 'entity',
    annotationFor: 'annotationFor',
    creatorId: 'creatorId',
    roles: 'roles',
    categories: 'categories',
    termset: 'termset',
    managedProduction: 'managedProduction',
    runtime: 'runtime',
    contextTerm: 'contextTerm',
    contextEntityType: 'contextEntityType',
    contextEntityId: 'contextEntityId',
    name: 'name',
    firstName: 'firstName',
    lastName: 'lastName',
    production: 'production',
    productionId: 'productionId',
    workspace: 'workspace',
    plan: 'plan',
    type: 'type',
    location: 'location',
    referencedId: 'referencedId',
    public: 'public',
    inactive: 'inactive',
    reservable: 'reservable',
    template: 'template',
    group: 'group',
    projectStatus: 'projectStatus',
    state: 'state',
    labels: 'labels',
    tags: 'tags',
    allProductionsExceptPipeline: 'AllProductionsExceptPipeline',
    owned: 'owned',
    origin: 'origin',
    entityType: 'entityType',
    contextId: 'contextId',
    fetchAllDealsInATransaction: 'fetchAllDealsInATransaction',
    excludeOwnedBranchedProductions: 'excludeOwnedBranchedProductions',
    productionType: 'productionType',
    parentTransactionId: 'parentTransactionId',
    key: 'key',
    resourceId: 'resourceId',
    shortlistId: 'shortlistId'
} as const;

export const IMPORT_SHORTLIST_ERRORS = {
    personAlreadyExists: 'Person entry already exists',
    personSameNameDifferentEmail: 'Person with the same email but different name exists'
};

export const FETCH_POLICY_CONFIG = {
    longLivedFetchPolicy: 'cache-first',
    apolloFetchPolicy: 'no-cache' as Extract<FetchPolicy, 'no-cache'>
};

export const DEFAULT_WIDGET = {
    defaultWidgetSize: 'small',
    defaultWidgetVariant: 'outlined'
};

export const CONSTANTS_ID = {
    createId: 'create',
    newEntityId: 'new'
};

export const INACTIVITY_TIMEOUT = {
    inactivityTimeout: 30 * 60, // 30 minutes
    inactivityDialogTimeout: 60 // 1 minute
};

export const DATA_GRID_CONSTANTS = {
    rowsPerPageOptions: [24, 48, 96],
    defaultPageNumber: 0,
    defaultPageSize: 24
};

export const LIST_VIEW_CONSTANTS = {
    listView: 'List',
    gridView: 'Grid',
    tableView: 'Table',
    scheduleView: 'Schedule',
    galleryView: 'Gallery',
    listPreview: 'List (Preview)',
    rankOrderView: 'Rank Order'
};

export const ACTIONS_CONSTANTS = {
    copy: 'copy',
    label: 'label'
};

export const MULTIPLE_SELECT_READ_ONLY = {
    lineBreak: 'lineBreak',
    pipe: 'pipe',
    comma: 'comma',
    chip: 'chip'
} as const;

export const ROUTE_CONSTANTS = {
    lineOperation: 'lineOperation',
    copyBooking: 'copyBooking',
    view: 'view',
    productionConnect: 'productionConnect',
    lastSelectedOrgId: 'lastSelectedOrgId'
};

export const STATE_CONSTANTS = {
    open: 'Open',
    closed: 'Closed'
};

export const MATH_SYMBOLS = {
    equalOperator: '=' as MathSymbols
};

export const PUNCTUATION_MARKS_CONSTANTS = {
    dash: '-',
    pipe: '|',
    comma: ',',
    period: '.',
    asterisk: '*',
    newLine: '\n',
    quotes: '""',
    underscore: '_',
    colon: ':',
    semicolon: ';'
};

export const BOOLEAN_STRINGS_CONSTANTS = {
    true: 'true',
    false: 'false'
};

export const SERVICE_RESPONSE_HANDLE_CONSTANTS = {
    fetchFailedMessage: 'Failed to fetch'
};

export const EVENT_TYPES = {
    ActivityEntityCreateEvent: 'ActivityEntityCreateEvent',
    ActivityEntityUpdateEvent: 'ActivityEntityUpdateEvent',
    ActivityEntityDeleteEvent: 'ActivityEntityDeleteEvent',
    ReservationEntityCreateEvent: 'ReservationEntityCreateEvent',
    ReservationEntityUpdateEvent: 'ReservationEntityUpdateEvent',
    ReservationEntityDeleteEvent: 'ReservationEntityDeleteEvent',
    PersonAnnotationCreationSuccessful: 'PersonAnnotationCreationSuccessful',
    OrganizationAnnotationCreationSuccessful: 'OrganizationAnnotationCreationSuccessful',
    InquirySentSuccessful: 'InquirySentSuccessful'
};

export const MEASUREMENT_UNIT_SHORTFORMS = {
    squareFoot: 'sq.ft.',
    squareMeter: 'sq.m.'
};

export const EVENT_LISTENERS = {
    beforeunload: 'beforeunload',
    resize: 'resize'
};

export const MEASUREMENT_UNIT_MAP = {
    'square foot': MEASUREMENT_UNIT_SHORTFORMS.squareFoot,
    'square meter': MEASUREMENT_UNIT_SHORTFORMS.squareMeter
};

export const MONEY_CONSTANTS = {
    EDITOR_FIELDS: {
        currencyCode: 'currencyCode',
        nanos: 'nanos',
        units: 'units'
    }
};

export const TIME_CONSTANTS = {
    hour: 'hour',
    d: 'd',
    h: 'h'
};

export const SIDE_PANEL_ID_LIST = {
    notes: 'notes',
    attachments: 'attachments',
    showrunnrAI: 'showrunnrAI'
};

export const logo = {
    smallLogo: 'smallLogo',
    bigLogo: 'bigLogo'
};

export const ALERT_VARIANTS = {
    success: 'success',
    info: 'info',
    warning: 'warning',
    error: 'error'
} as const;

export const KEY_CONSTANTS = {
    enter: 'Enter'
};

export const EMAIL_TYPE_STRING = {
    business: 'Business'
};

export const HASH_FUNCTION = {
    sha256: 'SHA-256'
};

export const SELECTION_MODEL_TYPES = {
    include: 'include',
    exclude: 'exclude'
} as const;

export const BUTTON_VARIANTS = {
    text: 'text',
    outlined: 'outlined',
    contained: 'contained'
} as const;

export const SELECT_VARIANTS = {
    standard: 'standard',
    outlined: 'outlined',
    filled: 'filled'
} as const;

export const CHIP_VARIANTS = {
    outlined: 'outlined',
    filled: 'filled'
} as const;

export const TOGGLE_PANEL_STATE = {
    close: 'Close',
    open: 'Open'
};
export const USER_ACTIONS = {
    click: 'click'
};

export const IMPORT_TYPE_KEYS = {
    email: 'email',
    website: 'website'
};

export const OWNER_ORGANIZATION_NAME = 'ownerOrganizationName';

export const INFO_TILES = {
    ownerOrganizationName: 'ownerOrganizationName',
    productionType: 'productionType',
    genre: 'genre'
};

export const SORTING_FIELDS = {
    updatedOnDesc: 'updatedOn_DESC',
    sequenceAsc: 'sequence_ASC',
    sequenceDesc: 'sequence_DESC'
};

export type SidePanelId = (typeof SIDE_PANEL_IDS)[keyof typeof SIDE_PANEL_IDS];

export const SIDE_PANEL_IDS = {
    notes: 'notes',
    attachments: 'attachments',
    statusReports: 'status-reports',
    showrunnrAI: 'showrunnrAI',
    keyCreditsAI: 'key-credits-ai',
    summaryPanel: 'summary-panel',
    attributes: 'attributes',
    tags: 'tags',
    approvals: 'approvals',
    reviewDeals: 'review-deals',
    productionDetails: 'production-details',
    productionNotes: 'production-notes',
    noteShare: 'note-share',
    share: 'share'
} as const;

export const LOCALE = 'locale';

export const WHITESPACE_REGEX = /\s/g;

export const COLOR_VARIANTS = {
    primary: 'primary',
    disabled: 'disabled',
    success: 'success',
    error: 'error'
} as const;

export const FILTER_EXPRESSIONS = {
    isSharedInDirectory: 'isSharedInDirectory',
    inactive: 'inactive'
};

export const INACTIVE_FALSE_FILTER = {
    expressions: [{ field: FILTER_EXPRESSIONS.inactive, value: { values: [BOOLEAN_STRINGS_CONSTANTS.false] } }]
};
export const FILE_TYPE = {
    pdf: 'pdf',
    dotPdf: '.pdf',
    png: 'png',
    PNG: 'PNG',
    xlsx: 'xlsx',
    imagePng: 'image/png',
    twoD: '2d',
    canvas: 'canvas',
    p: 'p',
    pt: 'pt',
    a4: 'a4'
};

export const SIZES = {
    medium: 'medium',
    small: 'small'
} as const;

export const TEMP_ID_PREFIX = 'temp-';

export const GENERAL_KEY_VALUES = {
    aka: 'aka'
};
export const SORT_DIRECTIONS = {
    asc: '_ASC',
    desc: '_DESC'
} as const;

export const INPUT_TYPES = {
    chips: 'chips',
    radio: 'radio'
};

export const PLACEHOLDER_CONSTANTS = {
    noDataToPreview: '-'
};

export const LAZY_LOAD_PAGE_SIZE = 96;

export const PEOPLE_PREVIEW_VIEWS = {
    associated: 'associated',
    hired: 'hired'
};

export const VISIBLE_CELL_CHIPS_COUNT = 3;

// Keep 10 or fewer options as a simple dropdown; search UI is only needed beyond that.
export const QUICK_FILTER_SEARCH_OPTIONS_THRESHOLD = 10;

export const ATTACHMENT_RENDERER_SKELETON_HEIGHT = 66;
